import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import {
  verificationResultSpecs,
  VerificationStatusSpecs,
  verificationStatusSpecs,
} from '@/data/customerVerificationStatuses';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useTranslator } from '@/hooks/useTranslator';
import { CustomerRow } from '@/Screens/Dashboard/Customers/types';

interface Props {
  readonly customer: CustomerRow;
  readonly field: keyof CustomerRow | 'name';
}

export const CustomerCellContent: React.FC<Props> = (props: Props): React.ReactElement => {
  const { customer, field } = props;
  const tr = useTranslator();
  const dateFormatter = useDateFormatter('MMM d, yyyy | HH:mm');

  switch (field) {
    case 'type':
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {tr(customer.type === 'INDIVIDUAL' ? 'INDIVIDUAL' : 'BUSINESS')}
        </Typography>
      );
    case 'name':
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {customer.type === 'INDIVIDUAL'
            ? `${customer.firstName} ${customer.lastName}`
            : customer.businessName}
        </Typography>
      );
    case 'verificationStatus': {
      const specs: VerificationStatusSpecs = verificationStatusSpecs(customer.verificationStatus);
      return (
        <Box sx={styles.cell}>
          <Box
            sx={{
              ...styles.badge,
              backgroundColor: specs.backgroundColor,
            }}>
            <Typography variant="body1" color={specs.color}>
              {tr(specs.label)}
            </Typography>
          </Box>
        </Box>
      );
    }
    case 'verificationResult': {
      const specs: VerificationStatusSpecs = verificationResultSpecs(customer.verificationResult);
      return (
        <Box sx={styles.cell}>
          <Box
            sx={{
              ...styles.badge,
              backgroundColor: specs.backgroundColor,
            }}>
            <Typography variant="body1" color={specs.color}>
              {tr(specs.label)}
            </Typography>
          </Box>
        </Box>
      );
    }
    case 'createdDate':
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {dateFormatter.parseAndFormat(customer.createdDate)}
        </Typography>
      );
    default:
      return (
        <Typography variant="body1" color="text.primary" sx={styles.cell}>
          {String(customer[field as keyof CustomerRow])}
        </Typography>
      );
  }
};

const styles: Record<string, SystemStyleObject> = {
  cell: {
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    px: 1.5,
    py: 0.5,
    borderRadius: 2,
  },
};
